/* Research Intelligence T3 — Research Evidence Panel.
 *
 * Operator-facing visibility surface over `evidence_records` +
 * `user_confirmation_queue` for ONE project. Lets the operator see:
 *   - 8 summary pills (total / approved / pending / rejected /
 *     revoked / queue / website-derived / manual-confirmed).
 *   - Filtered evidence rows + a separate confirmation queue tab.
 *   - Click any row to open `EvidenceInspector` for full provenance
 *     + approve / reject / revoke / resolve actions.
 *
 * Contract (T3 binding — see backend tests):
 *   - All actions only mutate `evidence_records` + `user_confirmation_queue`.
 *   - No agent prompts touched. No LLM calls. No source collection mutated.
 *   - Social rows clearly labelled "URL only — content not analyzed."
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Eye, RefreshCcw, FilterX } from "lucide-react";
import EvidenceInspector from "@/components/EvidenceInspector";

const STATE_BORDER = {
    approved: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
    pending:  "border-amber-500/30 text-amber-300 bg-amber-500/5",
    rejected: "border-red-500/30 text-red-300 bg-red-500/5",
    revoked:  "border-zinc-700 text-zinc-400 bg-zinc-900/40",
};

const PILL = "px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider";

function fmtValue(v) {
    if (v == null) return "—";
    if (typeof v === "string") return v.length > 80 ? v.slice(0, 80) + "…" : v;
    try {
        const s = JSON.stringify(v);
        return s.length > 80 ? s.slice(0, 80) + "…" : s;
    } catch { return String(v); }
}

export default function ResearchEvidencePanel({ projectId }) {
    const [evidence, setEvidence] = useState([]);
    const [summary, setSummary] = useState(null);
    const [queue, setQueue] = useState([]);
    const [tab, setTab] = useState("evidence");   // "evidence" | "queue"
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        approval_state: "",
        subject_kind: "",
        source_kind: "",
        include_revoked: false,
    });
    const [inspectorTarget, setInspectorTarget] = useState(null);   // {kind, row}

    const load = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.approval_state) params.set("approval_state", filters.approval_state);
            if (filters.subject_kind)   params.set("subject_kind", filters.subject_kind);
            if (filters.source_kind)    params.set("source_kind", filters.source_kind);
            if (filters.include_revoked) params.set("include_revoked", "true");
            const [eRes, qRes] = await Promise.all([
                api.get(`/research-intel/projects/${projectId}/evidence?${params.toString()}`),
                api.get(`/research-intel/projects/${projectId}/confirmation-queue`),
            ]);
            setEvidence(eRes.data?.items || []);
            setSummary(eRes.data?.summary || null);
            setQueue(qRes.data?.items || []);
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Failed to load evidence");
        } finally {
            setLoading(false);
        }
    }, [projectId, filters]);

    useEffect(() => { load(); }, [load]);

    const hasFilters = filters.approval_state || filters.subject_kind
        || filters.source_kind || filters.include_revoked;
    const clearFilters = () => setFilters({
        approval_state: "",
        subject_kind: "",
        source_kind: "",
        include_revoked: false,
    });

    const handleInspectorAction = () => { load(); };

    const subjectKinds = useMemo(
        () => Array.from(new Set(evidence.map((r) => r.subject_kind))).sort(),
        [evidence],
    );
    const sourceKinds = useMemo(
        () => Array.from(new Set(evidence.map((r) => r.source_kind))).sort(),
        [evidence],
    );

    return (
        <div className="mb-6 border border-zinc-800 rounded-sm bg-zinc-950/40"
             data-testid="research-evidence-panel">
            {/* ============ Header ============ */}
            <div className="flex items-center justify-between gap-3 p-3 border-b border-zinc-800">
                <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                        Research Evidence
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                        Operator visibility over discovered facts + the confirmation queue. No AI agent reads this yet.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="evidence-refresh-btn"
                    className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 rounded-sm disabled:opacity-40 transition-colors inline-flex items-center gap-1"
                >
                    <RefreshCcw className={"w-3 h-3 " + (loading ? "animate-spin" : "")} />
                    Refresh
                </button>
            </div>

            {/* ============ Summary Pills ============ */}
            {summary ? (
                <div className="p-3 flex flex-wrap gap-2 border-b border-zinc-800"
                     data-testid="evidence-summary-pills">
                    <Pill testid="ev-summary-total"     label="Total"      value={summary.total}      color="border-zinc-700 text-zinc-300 bg-zinc-900/40" />
                    <Pill testid="ev-summary-approved"  label="Approved"   value={summary.approved}   color={STATE_BORDER.approved} />
                    <Pill testid="ev-summary-pending"   label="Pending"    value={summary.pending}    color={STATE_BORDER.pending} />
                    <Pill testid="ev-summary-rejected"  label="Rejected"   value={summary.rejected}   color={STATE_BORDER.rejected} />
                    <Pill testid="ev-summary-revoked"   label="Revoked"    value={summary.revoked}    color={STATE_BORDER.revoked} />
                    <Pill testid="ev-summary-queue"     label="Queue"      value={summary.queue}      color="border-amber-500/30 text-amber-300 bg-amber-500/5" />
                    <Pill testid="ev-summary-website"   label="Website"    value={summary.website_derived}   color="border-sky-500/30 text-sky-300 bg-sky-500/5" />
                    <Pill testid="ev-summary-manual"    label="Manual ✓"   value={summary.manual_confirmed}  color="border-violet-500/30 text-violet-300 bg-violet-500/5" />
                </div>
            ) : null}

            {/* ============ Tabs ============ */}
            <div className="flex items-center gap-2 px-3 pt-3" data-testid="evidence-tabs">
                <TabBtn active={tab === "evidence"} onClick={() => setTab("evidence")}
                        testid="tab-evidence">
                    Evidence ({evidence.length})
                </TabBtn>
                <TabBtn active={tab === "queue"} onClick={() => setTab("queue")}
                        testid="tab-queue">
                    Confirmation Queue ({queue.length})
                </TabBtn>
            </div>

            {/* ============ Filter Bar (evidence only) ============ */}
            {tab === "evidence" ? (
                <div className="px-3 py-2 flex flex-wrap items-center gap-2 text-[10px]"
                     data-testid="evidence-filter-bar">
                    <FilterSelect
                        testid="filter-approval-state" label="State"
                        value={filters.approval_state}
                        onChange={(v) => setFilters((f) => ({ ...f, approval_state: v }))}
                        options={["", "pending", "approved", "rejected", "revoked"]} />
                    <FilterSelect
                        testid="filter-subject-kind" label="Subject"
                        value={filters.subject_kind}
                        onChange={(v) => setFilters((f) => ({ ...f, subject_kind: v }))}
                        options={["", ...subjectKinds]} />
                    <FilterSelect
                        testid="filter-source-kind" label="Source"
                        value={filters.source_kind}
                        onChange={(v) => setFilters((f) => ({ ...f, source_kind: v }))}
                        options={["", ...sourceKinds]} />
                    <label className="inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-800 rounded-sm cursor-pointer text-zinc-400">
                        <input type="checkbox" checked={filters.include_revoked}
                               onChange={(e) => setFilters((f) => ({ ...f, include_revoked: e.target.checked }))}
                               data-testid="filter-include-revoked" />
                        Include revoked
                    </label>
                    {hasFilters ? (
                        <button type="button" onClick={clearFilters}
                                data-testid="clear-filters-btn"
                                className="inline-flex items-center gap-1 px-2 py-1 text-zinc-400 hover:text-zinc-200 border border-zinc-800 rounded-sm">
                            <FilterX className="w-3 h-3" /> Clear
                        </button>
                    ) : null}
                </div>
            ) : null}

            {/* ============ Body ============ */}
            <div className="px-3 pb-3">
                {tab === "evidence" ? (
                    <EvidenceTable rows={evidence} onInspect={(row) => setInspectorTarget({ kind: "evidence", row })} />
                ) : (
                    <QueueTable rows={queue} onInspect={(row) => setInspectorTarget({ kind: "queue", row })} />
                )}
            </div>

            <EvidenceInspector
                target={inspectorTarget}
                projectId={projectId}
                onClose={() => setInspectorTarget(null)}
                onAction={handleInspectorAction}
            />
        </div>
    );
}

function Pill({ label, value, color, testid }) {
    return (
        <span className={PILL + " " + color} data-testid={testid}>
            {label}: {value ?? 0}
        </span>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={
                "px-3 py-1 text-[10px] font-mono uppercase tracking-wider rounded-sm border transition-colors " +
                (active
                    ? "border-zinc-500 text-zinc-100 bg-zinc-900"
                    : "border-zinc-800 text-zinc-500 hover:text-zinc-300")
            }
        >
            {children}
        </button>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <label className="inline-flex items-center gap-1 text-zinc-500">
            <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-[11px] rounded-sm px-1.5 py-0.5"
            >
                {options.map((o) => (
                    <option key={o} value={o}>{o || "All"}</option>
                ))}
            </select>
        </label>
    );
}

function EvidenceTable({ rows, onInspect }) {
    if (!rows.length) {
        return (
            <div className="py-6 text-center text-xs text-zinc-500" data-testid="evidence-empty">
                No evidence rows yet. Run Multi-Page Discovery to populate.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto" data-testid="evidence-table">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-zinc-500 text-[10px] font-mono uppercase tracking-wider border-b border-zinc-800">
                        <Th>Predicate</Th>
                        <Th>Value</Th>
                        <Th>Subject</Th>
                        <Th>Source</Th>
                        <Th>Confidence</Th>
                        <Th>State</Th>
                        <Th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/30"
                            data-testid={`evidence-row-${r.id}`}>
                            <Td mono>{r.predicate}</Td>
                            <Td>{fmtValue(r.object_value || r.fact_text)}</Td>
                            <Td mono>{r.subject_kind}</Td>
                            <Td mono>
                                {r.source_kind}
                                {r.source_location && r.source_location !== "n/a" ? (
                                    <span className="text-zinc-600"> · {r.source_location}</span>
                                ) : null}
                            </Td>
                            <Td>{(r.confidence ?? 0).toFixed(2)}</Td>
                            <Td>
                                <span className={"font-mono text-[10px] px-1.5 py-0.5 border rounded-sm " + (STATE_BORDER[r.approval_state] || "")}
                                      data-testid={`evidence-row-${r.id}-state`}>
                                    {r.approval_state || "pending"}
                                </span>
                            </Td>
                            <Td>
                                <button
                                    type="button"
                                    onClick={() => onInspect(r)}
                                    data-testid={`evidence-row-${r.id}-inspect`}
                                    className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 rounded-sm inline-flex items-center gap-1"
                                >
                                    <Eye className="w-3 h-3" /> Inspect
                                </button>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function QueueTable({ rows, onInspect }) {
    if (!rows.length) {
        return (
            <div className="py-6 text-center text-xs text-zinc-500" data-testid="queue-empty">
                No pending confirmations.
            </div>
        );
    }
    return (
        <div className="overflow-x-auto" data-testid="queue-table">
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-zinc-500 text-[10px] font-mono uppercase tracking-wider border-b border-zinc-800">
                        <Th>Kind</Th>
                        <Th>Summary</Th>
                        <Th>Linked evidence</Th>
                        <Th>Expires</Th>
                        <Th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-zinc-900 hover:bg-zinc-900/30"
                            data-testid={`queue-row-${r.id}`}>
                            <Td mono>
                                {r.kind}
                                {r.kind === "social_link" ? (
                                    <span className="ml-1 text-[9px] text-amber-300/80">(URL only)</span>
                                ) : null}
                            </Td>
                            <Td>{fmtValue(r.source_summary || r.candidate_payload)}</Td>
                            <Td mono>{(r.linked_evidence_ids || []).length}</Td>
                            <Td mono>{(r.expires_at || "").slice(0, 10) || "—"}</Td>
                            <Td>
                                <button
                                    type="button"
                                    onClick={() => onInspect(r)}
                                    data-testid={`queue-row-${r.id}-inspect`}
                                    className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 rounded-sm inline-flex items-center gap-1"
                                >
                                    <Eye className="w-3 h-3" /> Inspect
                                </button>
                            </Td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children }) {
    return <th className="text-left px-2 py-1.5 font-normal">{children}</th>;
}

function Td({ children, mono = false }) {
    return (
        <td className={"px-2 py-1.5 align-top " + (mono ? "font-mono text-[11px] text-zinc-300" : "text-zinc-200")}>
            {children}
        </td>
    );
}
